import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchCompareUserData, fetchUserBankAccounts, generateUserBankAccountTokenUrl } from '~/api/user-bank-accounts';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { InstitutionSource } from '~/types/institution';
import { User } from '~/types/user';
import {
  CompareUserData,
  UserBankAccount,
  UserBankAccountQueryKeys,
  UserBankAccountStatus,
} from '~/types/user-bank-account';

export const useUserBankAccountList = ({
  filters,
  enabled = true,
}: {
  filters: {
    source: InstitutionSource;
    statuses?: UserBankAccountStatus[];
    userId?: User['id'];
  };
  enabled?: boolean;
}) => {
  const companyId = useActiveCompanyId();
  const { source, statuses, userId } = filters;

  return useQuery<UserBankAccount[]>({
    queryKey: UserBankAccountQueryKeys.list({ ...filters, companyId }),
    queryFn: () => fetchUserBankAccounts({ companyId, source, statuses, userId }),
    enabled,
  });
};

export const useGenerateUserBankAccountTokenUrl = () =>
  useMutation({
    mutationFn: ({ id, includeEmail }: { id: UserBankAccount['id']; includeEmail: boolean }) =>
      generateUserBankAccountTokenUrl({ id, includeEmail }),
  });

export const useCompareUserDataQuery = (id: UserBankAccount['id']) =>
  useQuery<CompareUserData>({
    queryKey: UserBankAccountQueryKeys.compareUserData(id),
    queryFn: () => fetchCompareUserData(id),
  });
